import { input, TODO } from '@b2b/types/core';
import angular, { IOnChangesObject, ITimeoutService } from 'angular';
import { clamp } from 'lodash';

interface ViewModel {
  value: number;
  inputAddOn: string | null;
  label: string;
  max: number;
  min: number;
  ignoreValue: number;
}

interface ReservationItem {
  quantity: number;
  eventId?: string;
}

interface Reservation {
  id?: string;
  expirationTimestamp?: string;
  reservationItems?: ReservationItem[];
}

interface TimeoutCallbackPayload {
  callback: (value: boolean) => void;
  reservationId: string | undefined;
  hasTimedOut: boolean;
  timeoutIn: number;
}

interface TimeoutConfig {
  handler: angular.IPromise<void> | null;
  response: (value: boolean) => void;
  alertBeforeDefault: number;
}

interface ReservationServiceType {
  create: (items: ReservationItem[]) => Promise<TODO>;
  update: (reservation: Reservation) => Promise<TODO>;
  cancel: (id: string) => Promise<void>;
}

interface UtilsServiceType {
  removeUndefinedProperties: <T>(obj: T, useCurrentValue?: boolean) => Partial<T>;
}

interface ReservationApiResponse {
  success: boolean;
  payload?: Reservation;
  error?: TODO;
}

/**
 * Controller responsible for managing reservation-input component
 */
class ReservationInputController {
  static $inject = ['$timeout', 'ReservationService', 'UtilsService'];

  max = input<number>(99999);
  min = input<number>(0);
  ignoreValue = input<number>(0);
  value = input<number>(0);
  addOn = input<string>('');
  eventId = input<string>('');
  label = input<string>('');

  onBeforeTimeout!: (args: { $payload: TimeoutCallbackPayload }) => void;
  onInputChange!: (args: { $value: number }) => void;
  onReservationError!: (args: { $error: TODO }) => void;
  onReservationSuccess!: (args: { $reservationId: string }) => void;

  reservation: Reservation = {};
  hasErrors = false;
  showInputAddOn = false;
  vm!: ViewModel;
  timeout!: TimeoutConfig;

  constructor(
    private $timeout: ITimeoutService,
    private reservationService: ReservationServiceType,
    private utilsService: UtilsServiceType,
  ) {}

  /**
   * Angular lifecycle method $onInit
   * Ran when bindings are ready
   */
  $onInit(): void {
    this.reservation = {};
    this.hasErrors = false;
    this.showInputAddOn = false;
    this.vm = this.getVm();
    this.timeout = {
      handler: null,
      response: this.timeoutResponse.bind(this),
      alertBeforeDefault: 60000,
    };
  }

  /**
   * Angular lifecycle method $onChanges
   * Ran when watched bindings change
   */
  $onChanges(changes: IOnChangesObject): void {
    const update = this.utilsService.removeUndefinedProperties(changes, true);
    const updatedValue = (update as TODO).value;
    const skipOnChange = this.vm && (updatedValue === undefined || this.vm.value === updatedValue);

    Object.assign(this, update);
    this.vm = this.getVm();
    this.onChange(skipOnChange);
  }

  /**
   * Angular lifecycle method $onDestroy
   * Ran when component is about to be destroyed
   */
  $onDestroy(): void {
    const reservationId = this.reservation.id;

    if (reservationId) {
      this.reservationService.cancel(reservationId);
    }

    if (this.timeout.handler) {
      this.$timeout.cancel(this.timeout.handler);
    }
  }

  /**
   * Input onChange function handler
   * Ran when the DOM INPUT changes
   * Updates the viewModel & and alert via callbacks anyone watching it
   * Invokes onInputChange
   */
  onChange(skipOnChange?: boolean): void {
    const value = this.normalizeInput(this.vm.value);
    const reservation = this.reservation || {};
    const max = this.vm.max;
    const min = this.vm.min;
    const ignoreValue = this.vm.ignoreValue;

    if (skipOnChange) {
      return;
    }

    const clamped = clamp(value, min, max);
    const amount = clamp(clamped - ignoreValue, 0, max);
    this.vm.value = clamped;
    this.callbackHandler(this.onInputChange, { $value: clamped });

    if (amount > 0 || reservation.id) {
      // disable feature ENG-1199
      // this.reserveOrUpdateTicket(amount, reservation.id);
    }
  }

  /**
   * Manage the api call to reserve or update the ticket reservation
   * @private
   */
  private reserveOrUpdateTicket(amount: number, id?: string): Promise<TODO> {
    const eventId = this.eventId as string;
    const handler = this.ticketResponseHandler.bind(this);

    if (!id) {
      const reservationItems: ReservationItem[] = [
        {
          quantity: amount,
          eventId: eventId,
        },
      ];

      return this.reservationService.create(reservationItems).then(handler, handler);
    }

    const reservationItems = (this.reservation.reservationItems || []).map((reservationItem) => {
      reservationItem.quantity = amount;
      return reservationItem;
    });

    return this.reservationService
      .update(Object.assign(this.reservation, { reservationItems: reservationItems }))
      .then(handler, handler);
  }

  /**
   * Helper function to ensure that input is always a number
   * @private
   */
  private normalizeInput(value: unknown): number {
    return parseInt(String(value), 10) || 0;
  }

  /**
   * Safely handles function invocations
   * Passing all the arguments received to the callback
   * @private
   */
  private callbackHandler<T>(callback: ((args: T) => void) | string, args: T): void {
    const _callback = typeof callback === 'string' ? (this as TODO)[callback] : callback;

    if (_callback && typeof _callback === 'function') {
      // $timeout service is used to refresh scopes
      // Avoiding a manual digest
      this.$timeout(() => {
        _callback(args);
      }, 0);
    }
  }

  /**
   * Computes the viewModel and returns it
   * @private
   */
  private getVm(initialData?: Partial<ReservationInputController>): ViewModel {
    const _data = initialData || this;
    const inputValue = this.normalizeInput(_data.value);
    const inputAddOn = (_data.addOn as string) || null;
    const label = (_data.label as string) || '';
    const max = this.normalizeInput(_data.max) || 99999;
    const min = this.normalizeInput(_data.min) || 0;
    const ignoreValue = this.normalizeInput(_data.ignoreValue) || 0;

    return {
      value: clamp(inputValue, min, max),
      inputAddOn: inputAddOn,
      label: label,
      max: Math.abs(max),
      min: Math.abs(min),
      ignoreValue: Math.abs(ignoreValue),
    };
  }

  /**
   * Helper function to compute the absolute difference between two dates in ms
   * @private
   */
  private getTimeDiff(dateA: Date, dateB: Date, addToDiff?: number): number {
    const extraAmount = addToDiff || 0;
    const diff = Math.abs(dateA.getTime() - dateB.getTime()) + extraAmount;

    return diff;
  }

  /**
   * The callback that will be used to know if the user wants to renew reservations before timing out
   * @private
   */
  private timeoutResponse(value: boolean): void {
    if (value) {
      this.onChange();
    }
  }

  /**
   * Cancels and computes the timeout that will be invoked X ms time before the ticket expires
   * It depends on the alertBeforeDefault config
   * @private
   */
  private handleTimeout(now: Date, expirationString: string): void {
    const expirationDate = new Date(expirationString);
    const alertBefore = this.timeout.alertBeforeDefault;
    const callback = this.onBeforeTimeout;

    if (isNaN(expirationDate.valueOf())) {
      return;
    }

    const timeout = this.getTimeDiff(now, expirationDate, -alertBefore);
    if (this.timeout.handler) {
      this.$timeout.cancel(this.timeout.handler);
    }
    this.setTimeout(timeout > 0 ? timeout : 0, callback);
  }

  /**
   * Effectively sets the timeout using angular $timeout service
   * @private
   */
  private setTimeout(timeout: number, callback: (args: { $payload: TimeoutCallbackPayload }) => void): void {
    const hasTimedOut = !timeout;

    const result: TimeoutCallbackPayload = {
      callback: this.timeout.response,
      reservationId: this.reservation.id,
      hasTimedOut: hasTimedOut,
      timeoutIn: timeout,
    };

    const handler = () => {
      this.callbackHandler(callback, { $payload: result });
    };

    this.timeout.handler = this.$timeout(handler, timeout);
  }

  /**
   * Handles the response received from the reservation service
   * Invokes onReservationSuccess or onReservationError based on the response
   * @private
   */
  private ticketResponseHandler(response: ReservationApiResponse): void {
    const _response = response || {};
    const now = new Date();
    const success = !!_response.success;
    const payload = _response.payload;
    const error = _response.error;
    const reservation = this.reservation;

    if (success && payload) {
      this.reservation = Object.assign(reservation, payload);
      if (reservation.expirationTimestamp) {
        this.handleTimeout(now, reservation.expirationTimestamp);
      }
      this.callbackHandler(this.onReservationSuccess, { $reservationId: reservation.id || '' });
    } else {
      this.callbackHandler(this.onReservationError, { $error: error });
    }

    this.hasErrors = !success;
    this.showInputAddOn = !!(this.vm.inputAddOn && this.hasErrors);
  }
}

export const ReservationInputComponentModule = angular
  .module('acme.common.directive.reservationInput', ['acme.common.service.utils'])
  .component('acmeReservationInput', {
    templateUrl: 'common/components/reservation-input/reservation-input.tpl.html',
    controller: ReservationInputController,
    controllerAs: '$ctrl',
    bindings: {
      max: '<',
      min: '<',
      ignoreValue: '<',
      value: '<',
      addOn: '<',
      eventId: '<',
      label: '<',

      onBeforeTimeout: '&',
      onInputChange: '&',
      onReservationError: '&',
      onReservationSuccess: '&',
    },
  }).name;
